import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErroApi } from '../lib/api';
import { podeEscrever, useAutenticacao } from '../lib/auth';
import {
  buscarChamada,
  buscarPdDaEquipe,
  buscarRelatorio,
  criarSub,
  salvarPdDaEquipe,
  emReais,
  incluirLiderDoSub,
  salvarNumeroDoRelatorio,
  trocarDeSub,
  buscarEquipe,
  buscarEquipes,
  criarEquipe,
  incluirNaEquipe,
  marcarPresenca,
  soDigitos,
  tirarDaEquipe,
  type Culto,
  type EquipeResumo,
  type PessoaDaEquipe,
  type CampoDoRelatorio,
  type LinhaDoRelatorio,
  type PessoaNaChamada,
  type Sub,
} from '../lib/equipes';
import { iniciais } from '../lib/datas';
import { Alerta, Botao, Campo, Cartao } from '../componentes/Interface';
import { useConfirmacao } from '../componentes/Confirmacao';
import Layout from '../componentes/Layout';

/** "Semana de 18 a 24 de agosto", do jeito que a lideranca fala. */
function semanaEmPalavras(segunda: string): string {
  if (!segunda) return '';
  const inicio = new Date(`${segunda}T12:00:00-03:00`);
  const fim = new Date(inicio);
  fim.setDate(fim.getDate() + 6);
  const dia = (d: Date) =>
    new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: 'short' })
      .format(d)
      .replace('.', '');
  return `Semana de ${dia(inicio)} a ${dia(fim)}`;
}

const CORES = [
  'bg-tinta-400',
  'bg-tinta-900',
  'bg-amber-600',
  'bg-sky-700',
  'bg-violet-700',
  'bg-rose-600',
];

export default function Equipes() {
  const { usuario } = useAutenticacao();
  const daLideranca = podeEscrever(usuario?.papel);

  const [equipes, setEquipes] = useState<EquipeResumo[]>([]);
  const [aberta, setAberta] = useState<number | null>(null);
  const detalhe = useRef<HTMLDivElement>(null);
  // a equipe abre embaixo da lista: leva a pessoa ate ela (no celular a lista ocupa a tela toda)
  const abrirEquipe = (id: number) => {
    setAberta(id);
    setTimeout(() => detalhe.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };
  const [pessoas, setPessoas] = useState<PessoaDaEquipe[]>([]);
  const [subs, setSubs] = useState<Sub[]>([]);
  const [meusSubs, setMeusSubs] = useState<number[]>([]);
  const [podeMexerNosSubs, setPodeMexerNosSubs] = useState(false);
  const [subEscolhido, setSubEscolhido] = useState<number | ''>('');
  const [nomeSub, setNomeSub] = useState('');
  // um campo de cadastro por sub, guardado pela chave do bloco
  const [novoPorSub, setNovoPorSub] = useState<Record<string, { nome: string; telefone: string }>>({});
  const [novoLider, setNovoLider] = useState<Record<string, string>>({});
  const [nomeNovo, setNomeNovo] = useState('');
  const [telefoneNovo, setTelefoneNovo] = useState('');
  const [nomeEquipe, setNomeEquipe] = useState('');
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [aba, setAba] = useState<'pessoas' | 'chamada' | 'relatorio'>('pessoas');
  const [campos, setCampos] = useState<CampoDoRelatorio[]>([]);
  const [relatorio, setRelatorio] = useState<LinhaDoRelatorio[]>([]);
  const [salvandoCampo, setSalvandoCampo] = useState<string | null>(null);
  // Parceiro de Deus da equipe: null enquanto nao carregou (ou sem alcada)
  const [pdDaEquipe, setPdDaEquipe] = useState<number | null>(null);
  const [salvandoPd, setSalvandoPd] = useState(false);
  const [semana, setSemana] = useState<string>('');
  const [cultos, setCultos] = useState<Culto[]>([]);
  const [chamada, setChamada] = useState<PessoaNaChamada[]>([]);
  const [salvando, setSalvando] = useState<string | null>(null);
  const { confirmar, dialogo } = useConfirmacao();

  const carregar = useCallback(async () => {
    try {
      const r = await buscarEquipes();
      setEquipes(r.equipes);
      setErro(null);
      // quem lidera uma equipe so ja entra dentro dela
      if (r.equipes.length === 1) setAberta((atual) => atual ?? r.equipes[0]!.id);
    } catch (e) {
      // ARMADILHA 12: o motivo aparece, nao some
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar as equipes.');
    }
  }, []);

  const abrir = useCallback(async (id: number) => {
    try {
      const r = await buscarEquipe(id);
      setPessoas(r.pessoas);
      setSubs(r.subs);
      setMeusSubs(r.meusSubs);
      setPodeMexerNosSubs(r.podeMexerNosSubs);
      // quem lidera um sub so ja entra com ele escolhido
      setSubEscolhido((atual) =>
        atual && r.subs.some((x) => x.id === atual) ? atual : r.meusSubs[0] ?? '',
      );
      setAberta(id);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui abrir a equipe.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(() => {
    if (aberta) void abrir(aberta);
    // so quando muda a equipe escolhida
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberta]);

  const verChamada = useCallback(
    async (id: number, dia?: string) => {
      try {
        const r = await buscarChamada(id, dia);
        setChamada(r.pessoas);
        setCultos(r.cultos);
        setSemana(r.semana);
        setErro(null);
      } catch (e) {
        setErro(e instanceof ErroApi ? e.message : 'Não consegui abrir a chamada.');
      }
    },
    [],
  );

  const verRelatorio = useCallback(async (id: number, dia?: string) => {
    try {
      const r = await buscarRelatorio(id, dia);
      setRelatorio(r.subs);
      setCampos(r.campos);
      setSemana(r.semana);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui abrir o relatório.');
    }
  }, []);

  useEffect(() => {
    if (aberta && aba === 'chamada') void verChamada(aberta, semana || undefined);
    if (aberta && aba === 'relatorio') void verRelatorio(aberta, semana || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberta, aba]);

  // o Parceiro de Deus da equipe acompanha a semana aberta no relatorio;
  // so quem cuida da equipe (o 12, o ADM, o pastor, a administracao) ve
  useEffect(() => {
    if (!aberta || aba !== 'relatorio' || !podeMexerNosSubs || !semana) {
      setPdDaEquipe(null);
      return;
    }
    let vivo = true;
    buscarPdDaEquipe(aberta, semana)
      .then((r) => vivo && setPdDaEquipe(r.valor))
      .catch((e) => {
        // ARMADILHA 12: o motivo aparece, nao some
        if (vivo) setErro(e instanceof ErroApi ? e.message : 'Não consegui abrir o Parceiro de Deus.');
      });
    return () => {
      vivo = false;
    };
  }, [aberta, aba, semana, podeMexerNosSubs]);

  /** Guarda o Parceiro de Deus da semana. Lançar de novo substitui, nunca soma. */
  async function guardarPd(texto: string) {
    if (!aberta || !semana) return;
    const limpo = texto.replace(/[^0-9,.-]/g, '').replace(',', '.');
    const numero = Number(limpo || 0);
    if (Number.isNaN(numero) || numero < 0) return;
    const valor = Math.round(numero * 100);
    setSalvandoPd(true);
    try {
      const r = await salvarPdDaEquipe(aberta, { semana, valor });
      setPdDaEquipe(r.valor);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui guardar o Parceiro de Deus.');
    } finally {
      setSalvandoPd(false);
    }
  }

  /** Guarda UM número do relatório e já mostra a membresia recalculada. */
  async function guardarNumero(subId: number, campo: string, texto: string, dinheiro: boolean) {
    if (!aberta) return;
    const limpo = texto.replace(/[^0-9,.-]/g, '').replace(',', '.');
    const numero = Number(limpo || 0);
    if (Number.isNaN(numero) || numero < 0) return;
    const valor = dinheiro ? Math.round(numero * 100) : Math.round(numero);
    const chave = `${subId}:${campo}`;
    setSalvandoCampo(chave);
    try {
      const r = await salvarNumeroDoRelatorio(aberta, subId, { semana, campo, valor });
      setRelatorio((atual) =>
        atual.map((l) =>
          l.subId === subId ? { ...l, [campo]: valor, membresia: r.membresia } : l,
        ),
      );
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui guardar esse número.');
      await verRelatorio(aberta, semana);
    } finally {
      setSalvandoCampo(null);
    }
  }

  /** Marca UMA pessoa em UM culto e ja mostra na tela; sem salvar o resto. */
  async function alternar(pessoa: PessoaNaChamada, culto: string) {
    if (!aberta) return;
    const novo = !pessoa.presencas[culto];
    const chave = `${pessoa.id}:${culto}`;
    setSalvando(chave);
    setChamada((atual) =>
      atual.map((p) =>
        p.id === pessoa.id ? { ...p, presencas: { ...p.presencas, [culto]: novo } } : p,
      ),
    );
    try {
      await marcarPresenca(aberta, { membroId: pessoa.id, semana, culto, presente: novo });
      setErro(null);
    } catch (e) {
      // deu errado: a marca volta ao que era e o motivo aparece
      setChamada((atual) =>
        atual.map((p) =>
          p.id === pessoa.id ? { ...p, presencas: { ...p.presencas, [culto]: !novo } } : p,
        ),
      );
      setErro(e instanceof ErroApi ? e.message : 'Não consegui marcar a presença.');
    } finally {
      setSalvando(null);
    }
  }

  function andarSemana(dias: number) {
    if (!aberta || !semana) return;
    const base = new Date(`${semana}T12:00:00-03:00`);
    base.setDate(base.getDate() + dias);
    const nova = base.toISOString().slice(0, 10);
    if (aba === 'relatorio') void verRelatorio(aberta, nova);
    else void verChamada(aberta, nova);
  }

  const equipeAberta = useMemo(
    () => equipes.find((e) => e.id === aberta) ?? null,
    [equipes, aberta],
  );

  /** A equipe vista por sub: cada bloco com a gente dele e o campo de cadastro. */
  const blocos = useMemo(() => {
    const podeTudo = daLideranca || podeMexerNosSubs;
    const lista = subs
      .filter((sub) => podeTudo || meusSubs.includes(sub.id))
      .map((sub) => ({
        id: sub.id as number | null,
        chave: String(sub.id),
        nome: sub.nome,
        lideres: sub.lideres ?? [],
        pessoas: pessoas.filter((p) => p.subId === sub.id),
        podeIncluir: podeTudo || meusSubs.includes(sub.id),
      }));

    const semSub = pessoas.filter((p) => !p.subId);
    if (podeTudo && (semSub.length > 0 || subs.length === 0)) {
      lista.push({
        id: null,
        chave: 'sem-sub',
        nome: 'Sem sub',
        lideres: [],
        pessoas: semSub,
        podeIncluir: true,
      });
    }
    return lista;
  }, [subs, pessoas, meusSubs, podeMexerNosSubs, daLideranca]);

  function escrever(chave: string, campo: 'nome' | 'telefone', valor: string) {
    setNovoPorSub((atual) => ({
      ...atual,
      [chave]: { nome: '', telefone: '', ...(atual[chave] ?? {}), [campo]: valor },
    }));
  }

  /** Cadastra a pessoa direto embaixo do sub — uma pessoa por vez. */
  async function incluirNoSub(subId: number | null) {
    const chave = subId === null ? 'sem-sub' : String(subId);
    const dados = novoPorSub[chave];
    const nome = (dados?.nome ?? '').trim();
    if (!aberta || nome.length < 2) return;
    await comAviso(async () => {
      await incluirNaEquipe(aberta, {
        nome,
        telefone: (dados?.telefone ?? '').trim() || null,
        subId,
      });
      setNovoPorSub((atual) => ({ ...atual, [chave]: { nome: '', telefone: '' } }));
      if (aba === 'chamada') await verChamada(aberta, semana || undefined);
    });
  }

  async function comAviso(acao: () => Promise<unknown>) {
    setOcupado(true);
    try {
      await acao();
      await carregar();
      if (aberta) await abrir(aberta);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar.');
    } finally {
      setOcupado(false);
    }
  }

  async function incluir() {
    const nome = nomeNovo.trim();
    if (!aberta || nome.length < 2) return;
    await comAviso(async () => {
      await incluirNaEquipe(aberta, {
        nome,
        telefone: telefoneNovo.trim() || null,
        subId: subEscolhido === '' ? null : Number(subEscolhido),
      });
      setNomeNovo('');
      setTelefoneNovo('');
      // quem entra ja aparece na chamada da semana aberta
      if (aba === 'chamada') await verChamada(aberta, semana || undefined);
    });
  }

  async function tirar(p: PessoaDaEquipe) {
    if (!aberta) return;
    const certeza = await confirmar({
      titulo: `Tirar ${p.nome} da equipe?`,
      texto: 'A pessoa continua cadastrada na igreja, só sai desta equipe.',
      confirmar: 'Tirar da equipe',
      perigo: true,
    });
    if (!certeza) return;
    await comAviso(() => tirarDaEquipe(aberta, p.id));
  }

  return (
    <Layout
      titulo={daLideranca ? 'Equipes' : 'Equipe'}
      subtitulo={
        daLideranca
          ? 'As equipes da igreja, quem lidera cada uma e quem caminha junto.'
          : 'A sua equipe: quem caminha com você e o contato de cada um.'
      }
      acoes={
        daLideranca && (
          <Botao onClick={() => setCriando((v) => !v)} variante="secundario">
            {criando ? 'Cancelar' : '+ Nova equipe'}
          </Botao>
        )
      }
    >
      {dialogo}

      {erro && (
        <div className="mb-4 max-w-2xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}

      {criando && daLideranca && (
        <div className="mb-4 max-w-md">
          <Cartao>
            <Campo
              rotulo="Nome da equipe"
              value={nomeEquipe}
              onChange={(e) => setNomeEquipe(e.target.value)}
              placeholder="Ex.: Guerreiros"
            />
            <div className="mt-3">
              <Botao
                carregando={ocupado}
                onClick={() =>
                  comAviso(async () => {
                    await criarEquipe(nomeEquipe.trim());
                    setNomeEquipe('');
                    setCriando(false);
                  })
                }
              >
                Criar equipe
              </Botao>
            </div>
          </Cartao>
        </div>
      )}

      {equipes.length === 0 && !erro && (
        <div className="max-w-xl">
          <Alerta tipo="aviso">
            Você ainda não lidera nenhuma equipe por aqui. Fale com a liderança para te ligarem à
            sua equipe.
          </Alerta>
        </div>
      )}

      {equipes.length > 1 && (
        <div className="mb-6 grid grid-cols-2 gap-2 sm:gap-3 xl:grid-cols-3">
          {equipes.map((e, i) => (
            <button
              key={e.id}
              onClick={() => abrirEquipe(e.id)}
              className={`min-w-0 rounded-2xl bg-white p-3 text-left shadow-cartao transition hover:-translate-y-0.5 sm:p-4 ${
                aberta === e.id ? 'ring-2 ring-tinta-900' : ''
              }`}
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold text-white sm:h-11 sm:w-11 sm:text-sm ${
                    CORES[i % CORES.length]
                  }`}
                >
                  {iniciais(e.nome)}
                </span>
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-tinta-900">{e.nome}</span>
                  <span className="block truncate text-xs text-tinta-400">
                    {e.lideres.map((l) => l.nome).join(' e ') || 'sem líder'}
                  </span>
                </span>
              </div>
              <div className="mt-2 text-xs font-medium text-tinta-400 sm:mt-3">
                {e.pessoas} {e.pessoas === 1 ? 'pessoa' : 'pessoas'}
              </div>
            </button>
          ))}
        </div>
      )}

      <div ref={detalhe} className="scroll-mt-4" />
      {equipeAberta && (
        <Cartao>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-lg font-bold text-tinta-900">{equipeAberta.nome}</h2>
              <p className="text-sm text-tinta-400">
                Liderança: {equipeAberta.lideres.map((l) => l.nome).join(' e ') || 'a definir'}
              </p>
            </div>
            <span className="rounded-full bg-tinta-100 px-3 py-1 text-xs font-semibold text-tinta-600">
              {pessoas.length} {pessoas.length === 1 ? 'pessoa' : 'pessoas'}
            </span>
          </div>

          <div className="mt-4 flex max-w-sm rounded-2xl bg-tinta-100 p-1">
            {(
              [
                { valor: 'pessoas', rotulo: 'Pessoas' },
                { valor: 'chamada', rotulo: 'Chamada' },
                { valor: 'relatorio', rotulo: 'Relatório da semana' },
              ] as const
            ).map((t) => (
              <button
                key={t.valor}
                onClick={() => setAba(t.valor)}
                className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition ${
                  aba === t.valor ? 'bg-white text-tinta-900 shadow-cartao' : 'text-tinta-600'
                }`}
              >
                {t.rotulo}
              </button>
            ))}
          </div>

          {aba === 'pessoas' && (
            <div className="mt-4 space-y-4">
              {blocos.map((bloco) => (
                <div
                  key={bloco.id ?? 'sem-sub'}
                  className="overflow-hidden rounded-2xl border border-tinta-200"
                >
                  <div className="flex flex-wrap items-center gap-2 bg-tinta-900 px-4 py-3 text-white">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-bold uppercase tracking-wide">
                        {bloco.nome}
                      </span>
                      <span className="block truncate text-xs text-tinta-300">
                        {bloco.lideres.length
                          ? `líder: ${bloco.lideres.map((l) => l.nome).join(' e ')}`
                          : 'sem líder ainda'}
                      </span>
                    </span>
                    <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-bold">
                      {bloco.pessoas.length}{' '}
                      {bloco.pessoas.length === 1 ? 'pessoa' : 'pessoas'}
                    </span>
                  </div>

                  <ul className="divide-y divide-tinta-100 bg-white">
                    {bloco.pessoas.map((p) => (
                      <li key={p.id} className="flex items-center gap-3 px-4 py-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-tinta-900 text-xs font-bold text-white">
                          {iniciais(p.nome)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-tinta-900">
                            {p.nome}
                            {p.lidera && (
                              <span className="ml-2 rounded-full bg-tinta-900 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                                lidera
                              </span>
                            )}
                          </span>
                          <span className="block truncate text-xs text-tinta-400">
                            {p.telefone ?? 'sem telefone'}
                          </span>
                        </span>
                        {podeMexerNosSubs && subs.length > 0 && (
                          <select
                            value={p.subId ?? ''}
                            onChange={(e) =>
                              comAviso(() =>
                                trocarDeSub(
                                  aberta!,
                                  p.id,
                                  e.target.value ? Number(e.target.value) : null,
                                ),
                              )
                            }
                            className="hidden rounded-lg border border-tinta-200 bg-white px-2 py-1.5 text-xs text-tinta-700 sm:block"
                          >
                            <option value="">sem sub</option>
                            {subs.map((x) => (
                              <option key={x.id} value={x.id}>
                                {x.nome}
                              </option>
                            ))}
                          </select>
                        )}
                        {p.telefone && (
                          <a
                            href={`https://wa.me/55${soDigitos(p.telefone)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-lg bg-tinta-100 px-3 py-1.5 text-xs font-semibold text-tinta-900 hover:bg-tinta-200"
                          >
                            WhatsApp
                          </a>
                        )}
                        <button
                          onClick={() => tirar(p)}
                          className="rounded-lg px-2 py-1.5 text-xs font-medium text-tinta-400 hover:bg-tinta-100 hover:text-red-600"
                        >
                          tirar
                        </button>
                      </li>
                    ))}
                    {bloco.pessoas.length === 0 && (
                      <li className="px-4 py-4 text-sm text-tinta-400">
                        Ninguém aqui ainda. Escreva o nome logo abaixo.
                      </li>
                    )}
                  </ul>

                  {/* cada sub cadastra a propria gente, embaixo dele mesmo */}
                  {bloco.podeIncluir && (
                    <div className="grid gap-2 bg-tinta-50 px-4 py-3 sm:grid-cols-[1fr,1fr,auto] sm:items-center">
                      <input
                        value={novoPorSub[bloco.chave]?.nome ?? ''}
                        onChange={(e) => escrever(bloco.chave, 'nome', e.target.value)}
                        placeholder="Nome de quem entra"
                        className="rounded-xl border border-tinta-200 px-3 py-2 text-sm"
                      />
                      <input
                        value={novoPorSub[bloco.chave]?.telefone ?? ''}
                        onChange={(e) => escrever(bloco.chave, 'telefone', e.target.value)}
                        placeholder="WhatsApp (opcional)"
                        className="rounded-xl border border-tinta-200 px-3 py-2 text-sm"
                      />
                      <Botao carregando={ocupado} onClick={() => incluirNoSub(bloco.id)}>
                        Incluir
                      </Botao>
                    </div>
                  )}

                  {podeMexerNosSubs && bloco.id && (
                    <div className="flex flex-wrap items-center gap-2 border-t border-tinta-100 bg-white px-4 py-2">
                      <span className="text-xs text-tinta-400">Liderança do sub:</span>
                      {bloco.lideres.map((l) => (
                        <span
                          key={l.id}
                          className="rounded-lg bg-tinta-100 px-2 py-1 text-xs font-semibold text-tinta-700"
                        >
                          {l.nome}
                        </span>
                      ))}
                      <input
                        value={novoLider[bloco.chave] ?? ''}
                        onChange={(e) =>
                          setNovoLider((atual) => ({ ...atual, [bloco.chave]: e.target.value }))
                        }
                        placeholder="+ nome de quem lidera"
                        className="w-48 rounded-lg border border-tinta-200 px-2 py-1 text-xs"
                      />
                      <button
                        onClick={() =>
                          comAviso(async () => {
                            const nome = (novoLider[bloco.chave] ?? '').trim();
                            if (nome.length < 2 || !bloco.id) return;
                            await incluirLiderDoSub(aberta!, bloco.id, nome);
                            setNovoLider((atual) => ({ ...atual, [bloco.chave]: '' }));
                          })
                        }
                        className="rounded-lg bg-tinta-900 px-3 py-1 text-xs font-semibold text-white"
                      >
                        incluir
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {aba === 'chamada' && (
            <div className="mt-4">
              <div className="flex items-center justify-between gap-2">
                <Botao variante="suave" onClick={() => andarSemana(-7)}>
                  ‹ semana
                </Botao>
                <span className="text-sm font-semibold text-tinta-700">
                  {semanaEmPalavras(semana)}
                </span>
                <Botao variante="suave" onClick={() => andarSemana(7)}>
                  semana ›
                </Botao>
              </div>

              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[520px] border-separate border-spacing-y-1 text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-tinta-400">
                      <th className="px-2 pb-1">Pessoa</th>
                      {cultos.map((c) => (
                        <th key={c.chave} className="px-2 pb-1 text-center">
                          {c.nome}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {chamada.map((p) => (
                      <tr key={p.id} className="bg-tinta-50">
                        <td className="rounded-l-xl px-3 py-2">
                          <span className="block font-medium text-tinta-900">{p.nome}</span>
                          {p.subNome && (
                            <span className="block text-[11px] text-tinta-400">{p.subNome}</span>
                          )}
                        </td>
                        {cultos.map((c, i) => {
                          const marcado = !!p.presencas[c.chave];
                          const ocupadoAqui = salvando === `${p.id}:${c.chave}`;
                          return (
                            <td
                              key={c.chave}
                              className={`px-2 py-2 text-center ${
                                i === cultos.length - 1 ? 'rounded-r-xl' : ''
                              }`}
                            >
                              <button
                                onClick={() => alternar(p, c.chave)}
                                disabled={ocupadoAqui}
                                aria-label={`${p.nome} no culto de ${c.nome}`}
                                className={`h-9 w-9 rounded-xl border text-sm font-bold transition ${
                                  marcado
                                    ? 'border-tinta-900 bg-tinta-900 text-white'
                                    : 'border-tinta-200 bg-white text-tinta-300 hover:border-tinta-400'
                                } ${ocupadoAqui ? 'opacity-50' : ''}`}
                              >
                                {marcado ? '✓' : ''}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {chamada.length === 0 && (
                      <tr>
                        <td colSpan={cultos.length + 1} className="px-3 py-6 text-tinta-400">
                          Inclua as pessoas da equipe na aba ao lado para fazer a chamada.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {chamada.length > 0 && (
                    <tfoot>
                      <tr className="text-xs font-semibold text-tinta-500">
                        <td className="px-3 pt-2">Presentes</td>
                        {cultos.map((c) => (
                          <td key={c.chave} className="px-2 pt-2 text-center">
                            {chamada.filter((p) => p.presencas[c.chave]).length}/{chamada.length}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              <p className="mt-3 text-xs text-tinta-400">
                Cada toque salva sozinho, um de cada vez. Dá para voltar semanas e corrigir.
              </p>
            </div>
          )}

          {aba === 'relatorio' && (
            <div className="mt-4">
              <div className="flex items-center justify-between gap-2">
                <Botao variante="suave" onClick={() => andarSemana(-7)}>
                  ‹ semana
                </Botao>
                <span className="text-sm font-semibold text-tinta-700">
                  {semanaEmPalavras(semana)}
                </span>
                <Botao variante="suave" onClick={() => andarSemana(7)}>
                  semana ›
                </Botao>
              </div>

              {podeMexerNosSubs && pdDaEquipe !== null && (
                <div className="mt-4 rounded-2xl border border-tinta-200 bg-white p-4">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <label className="block text-sm sm:max-w-xs sm:flex-1">
                      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-tinta-400">
                        Parceiro de Deus da equipe (semana)
                      </span>
                      <input
                        key={`${aberta}:${semana}`}
                        defaultValue={(pdDaEquipe / 100).toFixed(2).replace('.', ',')}
                        // ARMADILHA 1: sai da caixinha, guarda so este numero
                        onBlur={(e) => void guardarPd(e.target.value)}
                        inputMode="decimal"
                        className={`w-full rounded-xl border px-3 py-2 text-sm ${
                          salvandoPd ? 'border-tinta-900 bg-tinta-100' : 'border-tinta-200'
                        }`}
                      />
                    </label>
                    <span className="text-right">
                      <span className="block text-[10px] uppercase tracking-wide text-tinta-400">
                        Lançado nesta semana
                      </span>
                      <span className="block text-xl font-black text-tinta-900">
                        {emReais(pdDaEquipe)}
                      </span>
                    </span>
                  </div>
                  <p className="mt-2 text-[11px] text-tinta-400">
                    O valor da equipe inteira, além dos PDs por sub abaixo. Lançar de novo substitui.
                  </p>
                </div>
              )}

              <div className="mt-4 space-y-4">
                {relatorio.map((linha) => (
                  <div key={linha.subId} className="rounded-2xl border border-tinta-200">
                    <div className="flex flex-wrap items-center justify-between gap-2 bg-tinta-900 px-4 py-3 text-white">
                      <span className="text-sm font-bold uppercase tracking-wide">{linha.nome}</span>
                      <span className="text-right">
                        <span className="block text-[10px] uppercase tracking-wide text-tinta-400">
                          Membresia
                        </span>
                        <span className="block text-xl font-black text-white">
                          {String(linha.membresia).replace('.', ',')}
                        </span>
                      </span>
                    </div>
                    <div className="grid gap-3 p-4 sm:grid-cols-3 lg:grid-cols-4">
                      {campos.map((campo) => {
                        const dinheiro = campo.tipo === 'dinheiro';
                        const bruto = Number(linha[campo.chave] ?? 0);
                        const chave = `${linha.subId}:${campo.chave}`;
                        return (
                          <label key={campo.chave} className="block text-sm">
                            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-tinta-400">
                              {campo.nome}
                            </span>
                            <input
                              defaultValue={dinheiro ? (bruto / 100).toFixed(2).replace('.', ',') : bruto}
                              // ARMADILHA 1: sai da caixinha, guarda so aquele numero
                              onBlur={(e) =>
                                guardarNumero(linha.subId, campo.chave, e.target.value, dinheiro)
                              }
                              inputMode="decimal"
                              className={`w-full rounded-xl border px-3 py-2 text-sm ${
                                salvandoCampo === chave
                                  ? 'border-tinta-900 bg-tinta-100'
                                  : 'border-tinta-200'
                              }`}
                            />
                            {dinheiro && (
                              <span className="mt-1 block text-[11px] text-tinta-400">
                                {emReais(bruto)}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {relatorio.length === 0 && (
                  <Alerta tipo="aviso">
                    Esta equipe ainda não tem sub. Crie um sub para preencher o relatório da semana.
                  </Alerta>
                )}
              </div>

              <p className="mt-3 text-xs text-tinta-400">
                Mesmas colunas da planilha. A membresia sai sozinha: domingo × 1, arena × 0,5 e
                culto da semana × 0,3. Cada número salva quando você sai da caixinha.
              </p>
            </div>
          )}

          {/* na chamada, o cadastro rapido continua aqui embaixo; na aba de
              pessoas cada sub tem o campo dele */}
          {aba === 'chamada' && (
            <>
          <div className="mt-4 grid gap-3 border-t border-tinta-100 pt-4 sm:grid-cols-[1fr,1fr,auto] sm:items-end">
            <Campo
              rotulo="Nome de quem entra"
              value={nomeNovo}
              onChange={(e) => setNomeNovo(e.target.value)}
              placeholder="Ex.: Maria Silva"
            />
            <Campo
              rotulo="WhatsApp (opcional)"
              value={telefoneNovo}
              onChange={(e) => setTelefoneNovo(e.target.value)}
              placeholder="(11) 90000-0000"
            />
            <Botao carregando={ocupado} onClick={incluir}>
              Incluir
            </Botao>
          </div>

          {subs.length > 0 && (
            <label className="mt-3 block max-w-xs text-sm">
              <span className="mb-1 block font-medium text-tinta-700">Entra no sub</span>
              <select
                value={subEscolhido}
                onChange={(e) => setSubEscolhido(e.target.value ? Number(e.target.value) : '')}
                className="w-full rounded-xl border border-tinta-200 bg-white px-3 py-2.5 text-sm"
              >
                {podeMexerNosSubs && <option value="">sem sub</option>}
                {subs
                  .filter((x) => podeMexerNosSubs || meusSubs.includes(x.id))
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.nome}
                      {x.liderNome ? ` · ${x.liderNome}` : ''}
                    </option>
                  ))}
              </select>
            </label>
          )}
            </>
          )}

          {podeMexerNosSubs && (
            <div className="mt-4 border-t border-tinta-100 pt-4">
              <p className="text-sm font-semibold text-tinta-900">
                Subs da equipe{' '}
                <span className="font-normal text-tinta-400">({subs.length})</span>
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {subs.map((x) => (
                  <span
                    key={x.id}
                    className="rounded-xl bg-tinta-100 px-3 py-1.5 text-xs font-semibold text-tinta-700"
                  >
                    {x.nome}
                    <span className="ml-1 font-normal text-tinta-400">
                      {x.liderNome ? `· ${x.liderNome} ` : ''}
                      ({x.pessoas})
                    </span>
                  </span>
                ))}
                {subs.length === 0 && (
                  <span className="text-xs text-tinta-400">Nenhum sub nesta equipe ainda.</span>
                )}
              </div>
              <div className="mt-3 flex max-w-md gap-2">
                <input
                  value={nomeSub}
                  onChange={(e) => setNomeSub(e.target.value)}
                  placeholder="Nome do sub novo"
                  className="flex-1 rounded-xl border border-tinta-200 px-3 py-2 text-sm"
                />
                <Botao
                  variante="secundario"
                  carregando={ocupado}
                  onClick={() =>
                    comAviso(async () => {
                      if (nomeSub.trim().length < 2) return;
                      await criarSub(aberta!, nomeSub.trim());
                      setNomeSub('');
                    })
                  }
                >
                  Criar sub
                </Botao>
              </div>
            </div>
          )}

        </Cartao>
      )}
    </Layout>
  );
}
